import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllEmpWorks,
  getMyEmpWorks,
  createEmpWork,
  updateEmpWork,
  deleteEmpWork,
  searchEmpWorks,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/empworkSlice";
import { Helmet } from "react-helmet-async";

const EmpWork = () => {
  const dispatch = useDispatch();
  const { empworks, loading, error, success, message } = useSelector(
    (state) => state.empwork
  );
  const { user } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    amount: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    amount: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    amount: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyEmpWorks, setShowMyEmpWorks] = useState(false);

  // Validation functions
  const validateName = (name) => {
    if (!name || !name.trim()) {
      return "EmpWork name is required";
    }
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      return "EmpWork name must be at least 2 characters";
    }
    if (trimmed.length > 100) {
      return "EmpWork name cannot exceed 100 characters";
    }
    return "";
  };

  const validateAmount = (amount) => {
    if (!amount && amount !== 0) {
      return "Amount is required";
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      return "Amount must be a valid number";
    }
    if (amountNum < 0) {
      return "Amount cannot be negative";
    }
    if (!Number.isFinite(amountNum)) {
      return "Amount must be a finite number";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "name":
        error = validateName(value);
        break;
      case "amount":
        error = validateAmount(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const nameValid = validateField("name", formData.name);
    const amountValid = validateField("amount", formData.amount);
    setTouched({ name: true, amount: true });
    return nameValid && amountValid;
  };

  // Load empworks on component mount
  useEffect(() => {
    if (showMyEmpWorks && user) {
      dispatch(getMyEmpWorks());
    } else {
      dispatch(getAllEmpWorks());
    }
  }, [dispatch, showMyEmpWorks, user]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "amount") {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, "");
      // Prevent multiple decimal points
      const parts = numericValue.split(".");
      const finalValue = parts.length > 2 
        ? parts[0] + "." + parts.slice(1).join("")
        : numericValue;
      
      setFormData((prev) => ({ ...prev, [name]: finalValue }));
      if (touched[name]) {
        validateField(name, finalValue);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const empworkData = {
      name: formData.name.trim(),
      amount: parseFloat(formData.amount),
    };

    try {
      if (isEditing) {
        await dispatch(updateEmpWork({ id: editingId, empworkData })).unwrap();
      } else {
        await dispatch(createEmpWork(empworkData)).unwrap();
      }
      
      resetForm();
      setShowModal(false);
      if (showMyEmpWorks && user) {
        dispatch(getMyEmpWorks());
      } else {
        dispatch(getAllEmpWorks());
      }
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", amount: "" });
    setErrors({ name: "", amount: "" });
    setTouched({ name: false, amount: false });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (empwork) => {
    setFormData({
      name: empwork.name,
      amount: empwork.amount.toString(),
    });
    setIsEditing(true);
    setEditingId(empwork._id);
    setShowModal(true);
    setErrors({ name: "", amount: "" });
    setTouched({ name: false, amount: false });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this empwork?")) {
      try {
        await dispatch(deleteEmpWork(id)).unwrap();
        if (showMyEmpWorks && user) {
          dispatch(getMyEmpWorks());
        } else {
          dispatch(getAllEmpWorks());
        }
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchEmpWorks(query));
      } else {
        if (showMyEmpWorks && user) {
          dispatch(getMyEmpWorks());
        } else {
          dispatch(getAllEmpWorks());
        }
      }
    },
    [dispatch, showMyEmpWorks, user]
  );

  // Toggle between all empworks and my empworks
  const handleToggleView = () => {
    setShowMyEmpWorks(!showMyEmpWorks);
    setSearchQuery("");
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `empwork-toast empwork-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("empwork-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("empwork-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);

  return (
    <>
      <Helmet>
        <title>Employee Work - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes empworkFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes empworkSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes empworkShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .empwork-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: empworkFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .empwork-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .empwork-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .empwork-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .empwork-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .empwork-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .empwork-search-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .empwork-search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .empwork-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .empwork-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .empwork-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .empwork-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .empwork-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .empwork-btn-secondary:hover {
          background: #cbd5e1;
        }

        .empwork-btn-toggle {
          background: #10b981;
          color: white;
        }

        .empwork-btn-toggle:hover {
          background: #059669;
        }

        .empwork-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
        }

        .empwork-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .empwork-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .empwork-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .empwork-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
        }

        .empwork-table tbody tr:hover {
          background: #f8f9fa;
        }

        .empwork-table tbody tr:last-child td {
          border-bottom: none;
        }

        .empwork-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .empwork-btn-edit {
          background: #10b981;
          color: white;
        }

        .empwork-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .empwork-btn-delete {
          background: #ef4444;
          color: white;
        }

        .empwork-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .empwork-user-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .empwork-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .empwork-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: empworkSlideIn 0.3s ease;
        }

        .empwork-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .empwork-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .empwork-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .empwork-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .empwork-form-group {
          margin-bottom: 20px;
        }

        .empwork-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .empwork-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .empwork-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .empwork-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .empwork-input.error {
          border-color: #dc2626;
          animation: empworkShake 0.5s ease;
        }

        .empwork-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .empwork-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .empwork-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .empwork-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .empwork-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .empwork-toast.empwork-toast-success {
          border-left: 4px solid #059669;
        }

        .empwork-toast.empwork-toast-error {
          border-left: 4px solid #dc2626;
        }

        .empwork-toast.empwork-toast-show {
          right: 20px;
          animation: empworkSlideIn 0.3s ease;
        }

        /* Mobile Responsive - Phone First */
        @media (max-width: 768px) {
          .empwork-container {
            padding: 15px;
          }

          .empwork-title {
            font-size: 2rem;
          }

          .empwork-card {
            padding: 20px;
            border-radius: 16px;
          }

          .empwork-search-bar {
            flex-direction: column;
          }

          .empwork-search-input {
            min-width: 100%;
          }

          .empwork-table {
            font-size: 0.9rem;
          }

          .empwork-table th,
          .empwork-table td {
            padding: 12px 8px;
          }

          .empwork-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .empwork-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .empwork-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .empwork-container {
            padding: 10px;
          }

          .empwork-title {
            font-size: 1.5rem;
          }

          .empwork-card {
            padding: 15px;
            border-radius: 12px;
          }

          .empwork-table {
            font-size: 0.85rem;
          }

          .empwork-table th,
          .empwork-table td {
            padding: 10px 6px;
          }

          .empwork-table th:nth-child(3),
          .empwork-table td:nth-child(3) {
            display: none;
          }

          .empwork-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
            display: block;
            width: 100%;
            margin-bottom: 8px;
            margin-right: 0;
          }

          .empwork-modal-content {
            padding: 15px;
            margin: 5px;
          }

          .empwork-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
            width: 100%;
            justify-content: center;
          }

          .empwork-search-bar {
            gap: 8px;
          }
        }

        @media (max-width: 360px) {
          .empwork-container {
            padding: 8px;
          }

          .empwork-title {
            font-size: 1.3rem;
          }

          .empwork-card {
            padding: 12px;
          }

          .empwork-table {
            font-size: 0.8rem;
          }

          .empwork-table th,
          .empwork-table td {
            padding: 8px 4px;
          }

          .empwork-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }

          .empwork-modal-content {
            padding: 12px;
          }

          .empwork-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
        }
      `}</style>

      <div className="empwork-container">
        <div className="empwork-wrapper">
          <div className="empwork-header">
            <h1 className="empwork-title">EmpWork Management</h1>
          </div>

          <div className="empwork-card">
            <div className="empwork-search-bar">
              <input
                type="text"
                className="empwork-search-input"
                placeholder="Search empworks by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {user && (
                <button
                  className="empwork-btn empwork-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyEmpWorks ? "Show All" : "Show My EmpWorks"}
                </button>
              )}
              <button
                className="empwork-btn empwork-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add EmpWork
              </button>
            </div>

            {loading && <div className="empwork-loading">Loading...</div>}

            {!loading && empworks.length === 0 && (
              <div className="empwork-empty">No empworks found</div>
            )}

            {!loading && empworks.length > 0 && (
              <div className="empwork-table-wrapper">
                <table className="empwork-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empworks.map((empwork) => (
                      <tr key={empwork._id}>
                        <td>{empwork.name}</td>
                        <td>₹{parseFloat(empwork.amount).toFixed(2)}</td>
                        <td>
                          {empwork.user ? (
                            <span className="empwork-user-badge">
                              {empwork.user.name || empwork.user.phone || "Unknown"}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {new Date(empwork.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className="empwork-action-btn empwork-btn-edit"
                            onClick={() => handleEdit(empwork)}
                          >
                            Edit
                          </button>
                          <button
                            className="empwork-action-btn empwork-btn-delete"
                            onClick={() => handleDelete(empwork._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="empwork-modal" onClick={() => setShowModal(false)}>
          <div
            className="empwork-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="empwork-modal-header">
              <h2 className="empwork-modal-title">
                {isEditing ? "Edit EmpWork" : "Add New EmpWork"}
              </h2>
              <button
                className="empwork-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="empwork-form-group">
                <label className="empwork-label required" htmlFor="name">
                  EmpWork Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("name")}
                  placeholder="Enter empwork name"
                  className={`empwork-input ${
                    errors.name && touched.name ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.name && touched.name && (
                  <div className="empwork-error">⚠️ {errors.name}</div>
                )}
              </div>

              <div className="empwork-form-group">
                <label className="empwork-label required" htmlFor="amount">
                  Amount
                </label>
                <input
                  id="amount"
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("amount")}
                  placeholder="Enter amount (e.g., 10000.50)"
                  className={`empwork-input ${
                    errors.amount && touched.amount ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.amount && touched.amount && (
                  <div className="empwork-error">⚠️ {errors.amount}</div>
                )}
              </div>

              <div className="empwork-modal-actions">
                <button
                  type="button"
                  className="empwork-btn empwork-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="empwork-btn empwork-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default EmpWork;

